import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  ChevronRight,
  Copy,
  ExternalLink,
  File as FileIcon,
  FileBox,
  FolderClosed,
  Loader2,
  MoreHorizontal,
  PackagePlus,
  Plug,
  RefreshCw,
  Scale,
  Search,
  Server,
  ShieldAlert,
  SlidersHorizontal,
  Trash2,
  Undo2,
  User,
  XOctagon,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import { formatBytes, formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { RequestPackageDialog } from '@/components/RequestPackageDialog'
import { StepUpButton } from '@/components/StepUpButton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BigNumber,
  EcosystemMark,
  Eyebrow,
} from '@/components/hawk-primitives'
import { StateBadge, StateCounts } from '@/components/state-badge'
import { RepoConnect } from '@/components/repo-connect'
import type { ClientSetup } from '@/lib/types/setups'

interface Repo {
  name: string
  ecosystem: string
  upstream_base: string | null
  effective_upstream_base: string
  mount_path: string
  package_count: number
  last_fetched_at: string | null
  setups: ClientSetup[]
}

interface RepoPackage {
  id: string
  name: string
  normalized_name: string
  ecosystem: string
  origin: 'upstream' | 'internal'
  upstream_synced_at: string | null
  file_count: number
  cached_file_count: number
  last_fetched_at: string | null
  cached_versions: string[]
  staging_count: number
  live_count: number
  quarantined_count: number
  deprecated_count: number
  blacklisted_count: number
  deleted_count: number
  purged_count: number
}

interface PackageFile {
  filename: string
  storage_path: string | null
  upstream_url: string | null
  size_bytes: number | null
  sha256: string | null
  upstream_sha256: string | null
  requires_python: string | null
  yanked: boolean
  yanked_locally: boolean
  tamper_detected: boolean
  tamper_detected_at: string | null
  fetched_at: string | null
  upstream_metadata_at: string | null
  created_at: string
  version_id: string | null
  version_state: import('@/lib/types/lifecycle').PackageLifecycleState | null
}

interface PackageDetail {
  package: {
    id: string
    name: string
    normalized_name: string
    ecosystem: string
    origin: string
    upstream_synced_at: string | null
  }
  files: PackageFile[]
}

interface RepoSearch {
  q?: string
  state?: import('@/lib/types/lifecycle').PackageLifecycleState
}

export const Route = createFileRoute('/_app/packages/$repo')({
  validateSearch: (s: Record<string, unknown>): RepoSearch => ({
    q: typeof s.q === 'string' ? s.q : undefined,
    state:
      typeof s.state === 'string' &&
      ['staging', 'live', 'quarantined', 'deprecated', 'blacklisted', 'deleted', 'purged'].includes(s.state)
        ? (s.state as import('@/lib/types/lifecycle').PackageLifecycleState)
        : undefined,
  }),
  component: RepoDetailPage,
})

function RepoDetailPage() {
  const { repo: repoName } = Route.useParams()
  const { q: initialQ, state: initialState } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { hasOrgRole, currentOrgSlug } = useAuth()
  const isAdmin = hasOrgRole('admin')
  const [search, setSearch] = useState(initialQ ?? '')
  const stateFilter = initialState ?? 'all'
  const setStateFilter = (next: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        state: next === 'all' ? undefined : (next as import('@/lib/types/lifecycle').PackageLifecycleState),
      }),
    })
  }
  const [requestOpen, setRequestOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const repos = useOrgQuery<Repo[]>(['admin-repositories'], '/admin/repositories')

  const packages = useOrgQuery<RepoPackage[]>(
    ['repo-packages', repoName],
    `/admin/repositories/${encodeURIComponent(repoName)}/packages`,
  )

  const repo = repos.data?.find((r) => r.name === repoName)
  if (repos.data && !repo) throw notFound()

  const parsedQuery = useMemo(() => parseSearch(search), [search])

  const filtered = useMemo(() => {
    const all = packages.data ?? []
    const { name, version } = parsedQuery
    return all.filter((p) => {
      if (stateFilter !== 'all' && countForState(p, stateFilter) === 0) return false
      if (!name && !version) return true
      if (version !== null) {
        return (
          p.normalized_name.includes(name) &&
          p.cached_versions.some((v) => v.toLowerCase().includes(version))
        )
      }
      return (
        p.normalized_name.includes(name) ||
        p.cached_versions.some((v) => v.toLowerCase().includes(name))
      )
    })
  }, [packages.data, parsedQuery, stateFilter])

  const autoExpandKeys = useMemo(() => {
    const { name, version } = parsedQuery
    if (!name && !version) return new Set<string>()
    const keys = new Set<string>()
    for (const p of packages.data ?? []) {
      if (version !== null) {
        if (
          p.normalized_name.includes(name) &&
          p.cached_versions.some((v) => v.toLowerCase().includes(version))
        ) {
          keys.add(p.normalized_name)
        }
      } else {
        const nameHit = p.normalized_name.includes(name)
        const versionHit = p.cached_versions.some((v) =>
          v.toLowerCase().includes(name),
        )
        if (!nameHit && versionHit) keys.add(p.normalized_name)
      }
    }
    return keys
  }, [packages.data, parsedQuery])

  const activeVersionTerm = parsedQuery.version ?? parsedQuery.name

  const totalPackages = packages.data?.length ?? 0

  return (
    <div className="px-10 py-8">
      <Link
        to="/packages"
        className="group inline-flex items-center gap-1.5 mb-5 -ml-1 px-2 py-1 rounded font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft
          className="size-3.5 group-hover:-translate-x-0.5 transition-transform"
          strokeWidth={1.5}
        />
        Back to packages
      </Link>

      <PageHeader
        title={repoName}
        right={
          repo && (
            <RepoActions
              repo={repo}
              onRequestClick={() => setRequestOpen(true)}
            />
          )
        }
      />

      {repo && (
        <RequestPackageDialog
          open={requestOpen}
          onOpenChange={setRequestOpen}
          repo={{
            name: repo.name,
            ecosystem: repo.ecosystem,
            upstream_base: repo.upstream_base,
          }}
        />
      )}

      {/* Folder header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
          <FolderClosed className="size-3.5 text-primary" strokeWidth={1.5} />
          <Link
            to="/packages"
            className="hover:text-foreground transition-colors"
          >
            packages
          </Link>
          <ChevronRight className="size-3 text-muted-foreground/40" />
          <span className="text-foreground/80">{repoName}</span>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
          {filtered.length.toString().padStart(3, '0')} / {totalPackages.toString().padStart(3, '0')} packages
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="fastapi, or 0.136, or fastapi@0.136"
            className="pl-9 bg-secondary border-transparent focus:border-primary/30 font-mono text-xs"
          />
        </div>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-44 bg-secondary border-transparent">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="staging">Staging</SelectItem>
            <SelectItem value="live">Live</SelectItem>
            <SelectItem value="quarantined">Quarantined</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
            <SelectItem value="blacklisted">Blacklisted</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
            <SelectItem value="purged">Purged</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Folder list */}
      {packages.isLoading ? (
        <PackageListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyPackages hasSearch={!!search} repoName={repoName} />
      ) : (
        <div className="border border-border bg-card rounded-md overflow-hidden">
          {/* Column header */}
          <div className="grid gap-4 px-5 py-3 border-b border-border bg-background/40 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground grid-cols-[1fr_120px_140px_44px]">
            <span>Name</span>
            <span className="text-right">Files</span>
            <span>Last fetched</span>
            <span />
          </div>
          <ul>
            {filtered.map((pkg, i) => {
              const open =
                expanded.has(pkg.normalized_name) ||
                autoExpandKeys.has(pkg.normalized_name)
              return (
                <PackageRow
                  key={pkg.normalized_name}
                  pkg={pkg}
                  repoName={repoName}
                  isAdmin={isAdmin}
                  delayMs={Math.min(i * 8, 200)}
                  expanded={open}
                  onToggle={() => toggleExpanded(pkg.normalized_name)}
                  versionFilter={
                    autoExpandKeys.has(pkg.normalized_name)
                      ? activeVersionTerm
                      : null
                  }
                />
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function isHttpUrl(value: string | null | undefined): value is string {
  if (!value) return false
  try {
    const u = new URL(value)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

function RepoActions({
  repo,
  onRequestClick,
}: {
  repo: Repo
  onRequestClick: () => void
}) {
  const { currentOrgSlug } = useAuth()
  const [connectOpen, setConnectOpen] = useState(false)
  const base = `${window.location.origin}${repo.mount_path}`
  const upstreamHref = isHttpUrl(repo.effective_upstream_base)
    ? repo.effective_upstream_base
    : null

  return (
    <div className="flex items-center gap-2">
      <Button onClick={onRequestClick} size="sm" className="gap-2">
        <PackagePlus className="size-3.5" strokeWidth={1.5} />
        Request package
      </Button>
      {(repo.setups?.length ?? 0) > 0 && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConnectOpen(true)}
            className="gap-2"
          >
            <Plug className="size-3.5" strokeWidth={1.5} />
            Connect
          </Button>
          <Sheet open={connectOpen} onOpenChange={setConnectOpen}>
            <SheetContent side="right" className="sm:max-w-lg">
              <ConnectSheetHeader repo={repo} />
              <div className="flex-1 overflow-y-auto px-5 py-6">
                <RepoConnect
                  setups={repo.setups}
                  base={base}
                  orgSlug={currentOrgSlug ?? ''}
                  repoName={repo.name}
                />
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}
      {upstreamHref && (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
        >
          <a
            href={upstreamHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
            Upstream
          </a>
        </Button>
      )}
    </div>
  )
}

function ConnectSheetHeader({ repo }: { repo: Repo }) {
  return (
    <SheetHeader className="px-5 pt-5 pb-4 border-b border-border bg-gradient-to-b from-card to-card/40 gap-3">
      <div className="flex items-center gap-3">
        <EcosystemMark ecosystem={repo.ecosystem} size="md" />
        <div className="min-w-0 flex-1">
          <SheetTitle className="text-base tracking-tight truncate">
            Connect to {repo.name}
          </SheetTitle>
          <SheetDescription className="text-xs mt-0.5">
            Point your package manager at this Hawk repo instead of the
            upstream registry.
          </SheetDescription>
        </div>
      </div>
    </SheetHeader>
  )
}

function PackageRow({
  pkg,
  repoName,
  isAdmin,
  delayMs,
  expanded,
  onToggle,
  versionFilter,
}: {
  pkg: RepoPackage
  repoName: string
  isAdmin: boolean
  delayMs: number
  expanded: boolean
  onToggle: () => void
  versionFilter: string | null
}) {
  const detail = useOrgQuery<PackageDetail>(
    ['repo-package-detail', repoName, pkg.normalized_name],
    `/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(pkg.normalized_name)}`,
    { enabled: expanded, staleTime: 60_000 },
  )

  const gridCols = 'grid-cols-[1fr_120px_140px_44px]'

  return (
    <li className="animate-hawk-rise" style={{ animationDelay: `${delayMs}ms` }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        aria-expanded={expanded}
        className={`group relative grid ${gridCols} gap-4 items-center px-5 py-3 cursor-pointer border-t border-border first:border-t-0 transition-colors ${
          expanded ? 'bg-popover/70' : 'hover:bg-popover'
        }`}
      >
        <span
          className={`absolute left-0 top-2 bottom-2 w-[2px] bg-primary transition-opacity ${
            expanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          aria-hidden
        />
        <div className="flex items-center gap-3 min-w-0">
          <EcosystemMark ecosystem={pkg.ecosystem} size="sm" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{pkg.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              {pkg.origin !== 'upstream' && (
                <div className="font-mono text-[10px] uppercase tracking-wider text-primary/70">
                  {pkg.origin}
                </div>
              )}
              <StateCounts counts={pkg} />
            </div>
          </div>
        </div>
        <div className="text-right font-mono text-xs tabular-nums text-muted-foreground">
          {pkg.cached_file_count.toLocaleString()}
        </div>
        <div className="font-mono text-xs text-muted-foreground tabular-nums">
          {pkg.last_fetched_at ? formatDate(pkg.last_fetched_at) : 'never'}
        </div>
        <div className="flex items-center justify-end">
          {isAdmin ? (
            <PackageActionsMenu
              repoName={repoName}
              packageName={pkg.normalized_name}
            />
          ) : (
            <ChevronRight
              className={`size-4 transition-all ${
                expanded
                  ? 'rotate-90 text-primary'
                  : 'text-muted-foreground/30 group-hover:text-muted-foreground'
              }`}
              strokeWidth={1.5}
            />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-background/40">
          <PackageVersionsTree
            pkg={pkg}
            detail={detail.data}
            isLoading={detail.isLoading}
            isError={detail.isError}
            versionFilter={versionFilter}
            isAdmin={isAdmin}
            repoName={repoName}
          />
        </div>
      )}
    </li>
  )
}

function PackageVersionsTree({
  pkg,
  detail,
  isLoading,
  isError,
  versionFilter,
  isAdmin,
  repoName,
}: {
  pkg: RepoPackage
  detail: PackageDetail | undefined
  isLoading: boolean
  isError: boolean
  versionFilter: string | null
  isAdmin: boolean
  repoName: string
}) {
  const versions = useMemo(() => {
    if (!detail) return [] as VersionGroup[]
    return groupFilesByVersion(detail.files, pkg.normalized_name, pkg.ecosystem)
  }, [detail, pkg.normalized_name, pkg.ecosystem])

  if (isLoading) {
    return (
      <div className="px-6 py-5 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
          Loading files
        </span>
      </div>
    )
  }
  if (isError) {
    return (
      <div className="px-6 py-5 text-sm text-destructive">
        Could not load package files.
      </div>
    )
  }
  if (versions.length === 0) {
    return (
      <div className="px-6 py-6 flex items-center gap-3">
        <FolderClosed
          className="size-4 text-muted-foreground/50 shrink-0"
          strokeWidth={1.5}
        />
        <div className="text-sm">
          <div className="text-foreground/80">No versions stored yet.</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Files appear here once a client downloads them through the proxy.
          </div>
        </div>
      </div>
    )
  }

  return (
    <ul>
      {versions.map((v) => (
        <VersionFolderRow
          key={v.version}
          group={v}
          repoName={repoName}
          packageName={pkg.normalized_name}
          isAdmin={isAdmin}
          defaultOpen={
            versionFilter !== null &&
            v.version.toLowerCase().includes(versionFilter)
          }
        />
      ))}
    </ul>
  )
}

interface VersionGroup {
  version: string
  files: PackageFile[]
  totalBytes: number
  allYanked: boolean
  /** True when every file at this version was yanked here, not upstream. */
  allYankedLocally: boolean
  someTampered: boolean
  state: import('@/lib/types/lifecycle').PackageLifecycleState | null
}

function groupFilesByVersion(
  files: PackageFile[],
  normalizedName: string,
  ecosystem: string,
): VersionGroup[] {
  const map = new Map<string, PackageFile[]>()
  for (const f of files) {
    const v = extractVersionFromFilename(f.filename, normalizedName, ecosystem) ?? '-'
    const arr = map.get(v) ?? []
    arr.push(f)
    map.set(v, arr)
  }
  const groups: VersionGroup[] = []
  for (const [version, vfiles] of map) {
    const effectiveYanked = (f: PackageFile) => f.yanked || f.yanked_locally
    groups.push({
      version,
      files: vfiles,
      totalBytes: vfiles.reduce((acc, f) => acc + (f.size_bytes ?? 0), 0),
      allYanked: vfiles.every(effectiveYanked),
      allYankedLocally: vfiles.every((f) => f.yanked_locally),
      someTampered: vfiles.some((f) => f.tamper_detected),
      state: vfiles.find((f) => f.version_state)?.version_state ?? null,
    })
  }
  groups.sort((a, b) => compareVersionsDesc(a.version, b.version))
  return groups
}

function extractVersionFromFilename(
  filename: string,
  normalizedName: string,
  ecosystem: string,
): string | null {
  if (ecosystem === 'pypi') {
    const exts = ['.whl', '.tar.gz', '.zip', '.tar.bz2']
    let stem = filename
    let isWheel = false
    for (const e of exts) {
      if (filename.endsWith(e)) {
        stem = filename.slice(0, -e.length)
        isWheel = e === '.whl'
        break
      }
    }
    const stemLower = stem.toLowerCase()
    for (let idx = 0; idx < stemLower.length; idx++) {
      if (stemLower[idx] !== '-') continue
      const prefix = stemLower.slice(0, idx).replace(/[_.]/g, '-')
      if (prefix === normalizedName) {
        const after = stem.slice(idx + 1)
        if (isWheel) {
          const dash = after.indexOf('-')
          return dash === -1 ? after : after.slice(0, dash)
        }
        return after
      }
    }
    return null
  }
  if (ecosystem === 'cargo') {
    if (!filename.endsWith('.crate')) return null
    const stem = filename.slice(0, -'.crate'.length)
    const prefix = `${normalizedName}-`
    if (!stem.startsWith(prefix)) return null
    const after = stem.slice(prefix.length)
    return after.length > 0 ? after : null
  }
  if (ecosystem === 'npm') {
    if (!filename.endsWith('.tgz')) return null
    const stem = filename.slice(0, -'.tgz'.length)
    const slash = normalizedName.lastIndexOf('/')
    const bare = slash >= 0 ? normalizedName.slice(slash + 1) : normalizedName
    const prefix = `${bare}-`
    if (!stem.startsWith(prefix)) return null
    const after = stem.slice(prefix.length)
    return after.length > 0 ? after : null
  }
  return null
}

function parseSearch(raw: string): { name: string; version: string | null } {
  const trimmed = raw.trim().toLowerCase()
  if (!trimmed) return { name: '', version: null }
  const explicit = trimmed.match(/^([^@:/=\s]+)\s*(?:==|@|:|\/)\s*(.*)$/)
  if (explicit) {
    const version = (explicit[2] ?? '').trim()
    return {
      name: explicit[1] ?? '',
      version: version.length > 0 ? version : null,
    }
  }
  const ws = trimmed.match(/^(\S+)\s+(.+)$/)
  if (ws) {
    return { name: ws[1] ?? '', version: (ws[2] ?? '').trim() || null }
  }
  return { name: trimmed, version: null }
}

function compareVersionsDesc(a: string, b: string): number {
  const parts = (v: string) =>
    v
      .split(/[^a-z0-9]/i)
      .filter(Boolean)
      .map((p) => {
        const n = Number(p)
        return Number.isFinite(n) && /^\d+$/.test(p) ? n : p
      })
  const pa = parts(a)
  const pb = parts(b)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const x = pa[i]
    const y = pb[i]
    if (x === undefined) return 1
    if (y === undefined) return -1
    if (typeof x === 'number' && typeof y === 'number') {
      if (x !== y) return y - x
    } else {
      const sx = String(x)
      const sy = String(y)
      if (sx !== sy) return sy.localeCompare(sx)
    }
  }
  return 0
}

function VersionFolderRow({
  group,
  repoName,
  packageName,
  isAdmin,
  defaultOpen = false,
}: {
  group: VersionGroup
  repoName: string
  packageName: string
  isAdmin: boolean
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <li className="border-t border-border first:border-t-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        aria-expanded={open}
        className={`group relative flex items-center gap-3 pl-12 pr-5 py-2 cursor-pointer transition-colors ${
          open ? 'bg-popover/40' : 'hover:bg-popover/60'
        }`}
      >
        <ChevronRight
          className={`size-3.5 transition-transform ${
            open ? 'rotate-90 text-primary' : 'text-muted-foreground/60'
          }`}
          strokeWidth={1.5}
        />
        <FolderClosed
          className={`size-3.5 ${open ? 'text-primary' : 'text-muted-foreground/60'}`}
          strokeWidth={1.5}
        />
        <span
          className={`font-mono text-[13px] tabular-nums ${
            group.allYanked
              ? 'text-muted-foreground line-through decoration-destructive/60'
              : 'text-foreground/90'
          }`}
        >
          {group.version}
        </span>
        {group.state && group.state !== 'live' && (
          <StateBadge state={group.state} size="xs" />
        )}
        {group.allYanked && (
          <YankedPill source={group.allYankedLocally ? 'local' : 'upstream'} />
        )}
        {group.someTampered && <TamperPill />}
        <span className="ml-auto flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="tabular-nums">
            {group.files.length} {group.files.length === 1 ? 'file' : 'files'}
          </span>
          {isAdmin && (
            <span className="tabular-nums w-16 text-right">
              {group.totalBytes > 0 ? formatBytes(group.totalBytes) : '-'}
            </span>
          )}
        </span>
        {isAdmin && (
          <VersionActionsMenu
            repoName={repoName}
            packageName={packageName}
            version={group.version}
            allYanked={group.allYanked}
            state={group.state}
          />
        )}
      </div>
      {open && (
        <ul className="pb-1">
          {group.files.map((f) => (
            <FileLeaf
              key={f.filename}
              file={f}
              isAdmin={isAdmin}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function YankedPill({ source }: { source: 'local' | 'upstream' }) {
  return (
    <span
      className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/30"
      title={
        source === 'local'
          ? 'An admin yanked this version here.'
          : 'Upstream marked this version yanked.'
      }
    >
      {source === 'local' ? 'Yanked' : 'Yanked upstream'}
    </span>
  )
}

function TamperPill() {
  return (
    <span
      className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-destructive/15 text-destructive ring-1 ring-inset ring-destructive/40"
      title="Upstream's hash for this file changed after we stored it. Local bytes are preserved; serving our copy."
    >
      <ShieldAlert className="size-2.5" strokeWidth={1.5} />
      Tamper detected
    </span>
  )
}

function FileLeaf({
  file,
  isAdmin,
}: {
  file: PackageFile
  isAdmin: boolean
}) {
  const gridCols = isAdmin
    ? 'grid-cols-[1fr_140px_120px]'
    : 'grid-cols-[1fr_140px]'
  return (
    <li
      className={`grid ${gridCols} gap-4 items-center pl-[88px] pr-5 py-1.5 text-xs hover:bg-popover/40 transition-colors`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileIcon
          className={`size-3 shrink-0 ${
            file.tamper_detected ? 'text-destructive' : 'text-primary'
          }`}
          strokeWidth={1.5}
        />
        <span
          className={`font-mono truncate ${
            file.yanked || file.yanked_locally
              ? 'text-muted-foreground line-through decoration-destructive/60'
              : 'text-foreground/80'
          }`}
          title={file.filename}
        >
          {file.filename}
        </span>
        {file.tamper_detected && <TamperPill />}
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] tabular-nums text-right">
        {file.tamper_detected ? (
          <span className="text-destructive">Drift</span>
        ) : file.yanked_locally ? (
          <span className="text-destructive/80">Yanked</span>
        ) : file.yanked ? (
          <span className="text-destructive/80">Yanked upstream</span>
        ) : null}
      </span>
      {isAdmin && (
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground text-right">
          {file.size_bytes ? formatBytes(file.size_bytes) : '-'}
        </span>
      )}
    </li>
  )
}

function HeroStat({
  label,
  value,
  unit,
  sub,
  isText,
}: {
  label: string
  value: string
  unit?: string
  sub: string
  isText?: boolean
}) {
  return (
    <div className="h-[160px] px-7 py-7 flex flex-col justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground leading-none">
        {label}
      </div>
      {isText ? (
        <div className="text-[28px] font-medium tracking-tight leading-none truncate">
          {value}
        </div>
      ) : (
        <BigNumber value={value} unit={unit} className="text-[44px]" />
      )}
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground leading-none truncate">
        {sub}
      </div>
    </div>
  )
}

function PackageListSkeleton() {
  return (
    <div className="border border-border bg-card rounded-md overflow-hidden">
      <div className="grid grid-cols-[1fr_120px_110px_140px_44px] gap-4 px-5 py-3 border-b border-border bg-background/40">
        <div className="h-3 w-12 bg-secondary rounded" />
        <div className="h-3 w-12 bg-secondary rounded ml-auto" />
        <div className="h-3 w-10 bg-secondary rounded ml-auto" />
        <div className="h-3 w-14 bg-secondary rounded" />
        <div />
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_120px_110px_140px_44px] gap-4 items-center px-5 py-3 border-t border-border hawk-shimmer"
        >
          <div className="flex items-center gap-3">
            <div className="size-6 bg-secondary rounded-[5px]" />
            <div className="h-3 w-32 bg-secondary rounded" />
          </div>
          <div className="h-3 w-10 bg-secondary rounded ml-auto" />
          <div className="h-3 w-12 bg-secondary rounded ml-auto" />
          <div className="h-3 w-16 bg-secondary rounded" />
          <div />
        </div>
      ))}
    </div>
  )
}

function EmptyPackages({
  hasSearch,
  repoName,
}: {
  hasSearch: boolean
  repoName: string
}) {
  return (
    <div className="relative overflow-hidden border border-border border-dashed rounded-md py-16 px-8 text-center bg-card/30">
      <div
        className="absolute inset-0 hawk-grid hawk-fade-radial pointer-events-none"
        aria-hidden
      />
      <div className="relative">
        <div className="inline-flex items-center justify-center size-12 rounded-md bg-secondary border border-border mb-5">
          <FileBox
            className="size-5 text-muted-foreground"
            strokeWidth={1.5}
          />
        </div>
        <p className="font-display text-2xl tracking-tight mb-2 text-foreground/80">
          {hasSearch ? 'No matches.' : 'Empty repository.'}
        </p>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {hasSearch
            ? 'Try a different search term.'
            : `No packages have been admitted into ${repoName} yet. Install something from this mount to populate the cache.`}
        </p>
      </div>
    </div>
  )
}

function humanEcosystem(eco: string): string {
  const map: Record<string, string> = {
    pypi: 'Python (PyPI)',
    npm: 'JavaScript (npm)',
    cargo: 'Rust (cargo)',
    maven: 'Java (Maven)',
    rubygems: 'Ruby (RubyGems)',
    nuget: '.NET (NuGet)',
    go: 'Go modules',
    docker: 'Docker images',
    composer: 'PHP (Composer)',
  }
  return map[eco.toLowerCase()] ?? eco
}

function formatBytesShort(bytes: number): { value: string; unit: string } {
  if (bytes === 0 || !Number.isFinite(bytes)) return { value: '0', unit: 'B' }
  const units = ['B', 'KIB', 'MIB', 'GIB', 'TIB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / Math.pow(1024, i)
  const formatted = value >= 100 || i === 0 ? value.toFixed(0) : value.toFixed(1)
  return { value: formatted, unit: units[i] ?? 'B' }
}

function useRefreshRepo(repoName: string) {
  const qc = useQueryClient()
  const { currentOrgSlug } = useAuth()
  return () => {
    qc.invalidateQueries({ queryKey: ['repo-packages', currentOrgSlug, repoName] })
    qc.invalidateQueries({ queryKey: ['repo-package-detail', currentOrgSlug, repoName] })
  }
}

function PackageActionsMenu({
  repoName,
  packageName,
}: {
  repoName: string
  packageName: string
}) {
  const refresh = useRefreshRepo(repoName)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const resync = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(
        orgPath(`/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(packageName)}/resync`),
        { method: 'POST' },
      )
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      toast.success(`Resync queued for ${packageName}`)
      refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Actions"
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => resync.mutate()} disabled={resync.isPending}>
            <RefreshCw className="size-3.5" strokeWidth={1.5} />
            Resync metadata
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <XOctagon className="size-3.5" strokeWidth={1.5} />
            Delete package
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeletePackageDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        repoName={repoName}
        packageName={packageName}
        onDeleted={refresh}
      />
    </span>
  )
}

function VersionActionsMenu({
  repoName,
  packageName,
  version,
  allYanked,
  state,
}: {
  repoName: string
  packageName: string
  version: string
  allYanked: boolean
  state: import('@/lib/types/lifecycle').PackageLifecycleState | null
}) {
  const refresh = useRefreshRepo(repoName)
  const [confirmYank, setConfirmYank] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [pendingTransition, setPendingTransition] = useState<
    import('@/lib/types/lifecycle').PackageLifecycleState | null
  >(null)

  const yank = useMutation({
    mutationFn: async (action: 'yank' | 'unyank') => {
      const r = await apiFetch(
        orgPath(`/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(packageName)}/versions/${encodeURIComponent(version)}/${action}`),
        { method: 'POST' },
      )
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: (_, action) => {
      toast.success(action === 'yank' ? `Yanked ${version}` : `Unyanked ${version}`)
      refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Actions"
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {availableTransitions(state).map((to) => (
            <DropdownMenuItem
              key={to}
              onClick={() => setPendingTransition(to)}
              variant={to === 'blacklisted' || to === 'deleted' ? 'destructive' : 'default'}
            >
              {transitionLabel(to)}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
            View history
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {allYanked ? (
            <DropdownMenuItem
              onClick={() => yank.mutate('unyank')}
              disabled={yank.isPending}
            >
              <Undo2 className="size-3.5" strokeWidth={1.5} />
              Unyank version
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setConfirmYank(true)}
            >
              <XOctagon className="size-3.5" strokeWidth={1.5} />
              Yank version
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <TransitionDialog
        repoName={repoName}
        packageName={packageName}
        version={version}
        from={state}
        to={pendingTransition}
        onClose={() => setPendingTransition(null)}
        onSuccess={refresh}
      />

      <TransitionHistoryDialog
        repoName={repoName}
        packageName={packageName}
        version={version}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />

      <AlertDialog open={confirmYank} onOpenChange={setConfirmYank}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Yank {packageName} {version}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Flags every file at this version as yanked. You can always undo
              a yank.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => yank.mutate('yank')}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Yank version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  )
}

function DeletePackageDialog({
  open,
  onOpenChange,
  repoName,
  packageName,
  onDeleted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  repoName: string
  packageName: string
  onDeleted: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {packageName}?</DialogTitle>
          <DialogDescription>
            Removes the package, its files, and stored bytes from this
            repository. Future installs will re-fetch from upstream if
            approved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <StepUpButton
            scope="package.delete"
            onAction={async () => {
              const r = await apiFetch(
                orgPath(`/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(packageName)}`),
                { method: 'DELETE' },
              )
              if (!r.ok && r.status !== 204) throw new Error(await r.text())
            }}
            onSuccess={() => {
              toast.success(`Deleted ${packageName}`)
              onDeleted()
              onOpenChange(false)
            }}
            onError={(msg) => toast.error(msg)}
            variant="danger"
          >
            <Trash2 className="size-4" strokeWidth={1.5} />
            Delete permanently
          </StepUpButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type LifecycleState = import('@/lib/types/lifecycle').PackageLifecycleState

function countForState(p: RepoPackage, state: string): number {
  switch (state) {
    case 'staging': return p.staging_count
    case 'live': return p.live_count
    case 'quarantined': return p.quarantined_count
    case 'deprecated': return p.deprecated_count
    case 'blacklisted': return p.blacklisted_count
    case 'deleted': return p.deleted_count
    case 'purged': return p.purged_count
    default: return 0
  }
}

function availableTransitions(from: LifecycleState | null): LifecycleState[] {
  if (!from) return ['live', 'quarantined', 'deprecated', 'blacklisted', 'deleted']
  switch (from) {
    case 'staging':
      return ['live', 'quarantined', 'deprecated', 'blacklisted', 'deleted']
    case 'live':
      return ['quarantined', 'deprecated', 'blacklisted', 'deleted']
    case 'quarantined':
      return ['live', 'staging', 'deprecated', 'blacklisted', 'deleted']
    case 'deprecated':
      return ['live', 'quarantined', 'blacklisted', 'deleted']
    case 'blacklisted':
      return ['staging', 'live', 'quarantined', 'deprecated', 'deleted']
    case 'deleted':
    case 'purged':
      return ['staging', 'live', 'quarantined', 'deprecated', 'blacklisted']
  }
}

function transitionLabel(to: LifecycleState): string {
  switch (to) {
    case 'live': return 'Promote to Live'
    case 'staging': return 'Demote to Staging'
    case 'quarantined': return 'Quarantine'
    case 'deprecated': return 'Deprecate'
    case 'blacklisted': return 'Blacklist'
    case 'deleted': return 'Delete'
    case 'purged': return 'Purge'
  }
}

function stepUpScope(from: LifecycleState | null, to: LifecycleState): string {
  if (from === 'blacklisted') return 'package.unblacklist'
  if (from === 'deleted' || from === 'purged') return 'package.undelete'
  if (to === 'deleted' || to === 'purged') return 'package.delete'
  if (to === 'staging' || to === 'live') return 'package.promote'
  if (to === 'quarantined') return 'package.quarantine'
  if (to === 'deprecated') return 'package.deprecate'
  return 'package.blacklist'
}

function TransitionHistoryDialog({
  repoName,
  packageName,
  version,
  open,
  onClose,
}: {
  repoName: string
  packageName: string
  version: string
  open: boolean
  onClose: () => void
}) {
  const q = useOrgQuery<import('@/lib/types/lifecycle').TransitionRow[]>(
    ['transitions', repoName, packageName, version],
    `/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(packageName)}/versions/${encodeURIComponent(version)}/transitions`,
    { enabled: open },
  )
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Transition history
          </div>
          <DialogTitle className="font-mono text-base tracking-tight">
            {packageName}
            <span className="text-muted-foreground/60">{' / '}</span>
            <span className="text-primary">{version}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          {q.isLoading && (
            <div className="text-muted-foreground font-mono text-[11px] uppercase tracking-[0.18em] py-10 text-center">
              Loading
            </div>
          )}
          {q.data && q.data.length === 0 && (
            <div className="text-muted-foreground font-mono text-[11px] uppercase tracking-[0.18em] py-10 text-center">
              No transitions recorded
            </div>
          )}
          {q.data && q.data.length > 0 && (
            <ol className="relative pl-7 pt-2">
              <div
                aria-hidden
                className="absolute left-[11px] top-3 bottom-3 w-px bg-border"
              />
              {q.data.map((t, i) => (
                <TransitionTimelineItem
                  key={t.id}
                  row={t}
                  isLast={i === q.data.length - 1}
                />
              ))}
            </ol>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TransitionTimelineItem({
  row,
  isLast,
}: {
  row: import('@/lib/types/lifecycle').TransitionRow
  isLast: boolean
}) {
  const source = parseTriggeredBy(row.triggered_by)
  const SourceIcon = source.icon
  const absolute = new Date(row.created_at).toLocaleString()
  return (
    <li className={`relative ${isLast ? 'pb-1' : 'pb-5'}`}>
      <span
        className={`absolute -left-7 top-1 size-[22px] rounded-full ring-1 ring-inset flex items-center justify-center ${source.dotClass}`}
        aria-hidden
      >
        <SourceIcon className="size-3" strokeWidth={1.75} />
      </span>
      <div className="flex items-center gap-2 mb-1">
        {row.from_state ? (
          <StateBadge state={row.from_state} size="xs" />
        ) : (
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60 px-1.5">
            initial
          </span>
        )}
        <ArrowRight className="size-3 text-muted-foreground/50" strokeWidth={1.75} />
        <StateBadge state={row.to_state} size="xs" />
        <span
          className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums"
          title={absolute}
        >
          {formatDate(row.created_at)}
        </span>
      </div>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
        <span className={source.labelClass}>{source.label}</span>
        {source.detail && (
          <span className="text-muted-foreground/50 normal-case tracking-normal">
            {source.detail}
          </span>
        )}
      </div>
      {row.reason && (
        <div className="text-[13px] text-foreground/85 mt-1.5 leading-relaxed">
          {row.reason}
        </div>
      )}
    </li>
  )
}

function parseTriggeredBy(triggered_by: string): {
  label: string
  detail?: string
  icon: typeof Bot
  dotClass: string
  labelClass: string
} {
  if (triggered_by === 'scrobe') {
    return {
      label: 'Scrobe',
      icon: Bot,
      dotClass: 'bg-amber-500/10 ring-amber-500/30 text-amber-500',
      labelClass: 'text-amber-500/90',
    }
  }
  if (triggered_by === 'policy_engine') {
    return {
      label: 'Policy engine',
      icon: Scale,
      dotClass: 'bg-primary/10 ring-primary/30 text-primary',
      labelClass: 'text-primary/90',
    }
  }
  if (triggered_by === 'system') {
    return {
      label: 'System',
      icon: Server,
      dotClass: 'bg-muted/40 ring-muted-foreground/30 text-muted-foreground',
      labelClass: 'text-muted-foreground',
    }
  }
  if (triggered_by.startsWith('admin:')) {
    return {
      label: 'Admin',
      detail: triggered_by.slice('admin:'.length, 'admin:'.length + 8),
      icon: User,
      dotClass: 'bg-primary/10 ring-primary/30 text-primary',
      labelClass: 'text-foreground/80',
    }
  }
  return {
    label: triggered_by,
    icon: Server,
    dotClass: 'bg-muted/40 ring-muted-foreground/30 text-muted-foreground',
    labelClass: 'text-muted-foreground',
  }
}

function TransitionDialog({
  repoName,
  packageName,
  version,
  from,
  to,
  onClose,
  onSuccess,
}: {
  repoName: string
  packageName: string
  version: string
  from: LifecycleState | null
  to: LifecycleState | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [reason, setReason] = useState('')
  if (!to) return null
  const scope = stepUpScope(from, to)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{transitionLabel(to)} {packageName} {version}</DialogTitle>
          <DialogDescription>
            Transition from{' '}
            <span className="font-mono uppercase">{from ?? 'unknown'}</span> to{' '}
            <span className="font-mono uppercase">{to}</span>. Optional reason
            is recorded on the transition log.
          </DialogDescription>
        </DialogHeader>
        <div className="py-3">
          <Input
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <StepUpButton
            scope={scope}
            onAction={async () => {
              const r = await apiFetch(
                orgPath(`/admin/repositories/${encodeURIComponent(repoName)}/packages/${encodeURIComponent(packageName)}/versions/${encodeURIComponent(version)}/state`),
                {
                  method: 'POST',
                  body: JSON.stringify({ to, reason: reason || null }),
                },
              )
              if (!r.ok) throw new Error(await r.text())
            }}
            onSuccess={() => {
              toast.success(`${packageName} ${version}: ${transitionLabel(to)}`)
              onSuccess()
              onClose()
            }}
          >
            {transitionLabel(to)}
          </StepUpButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
